import { CommonModule } from '@angular/common'
import { HttpClientModule } from '@angular/common/http'
import {
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  NgModule,
  NO_ERRORS_SCHEMA,
  provideAppInitializer,
} from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouterModule } from '@angular/router'
import { ICON_SETTINGS } from '@progress/kendo-angular-icons'
import { NgChartsModule } from 'ng2-charts'
import { FeatureToggleModule } from 'ngx-feature-toggle'
import { ImageCropperModule } from 'ngx-image-cropper'
import { MomentModule } from 'ngx-moment'

import {
  BadgeComponent,
  BannerComponent,
  ButtonComponent,
  CheckboxComponent,
  DialogService,
  ErrorComponent,
  HyperkitComponent,
  IconComponent,
  InputComponent,
  LinkComponent,
  MessageComponent,
  SearchInputComponent,
  SnackbarComponent,
  TextAreaComponent,
  ThemeService,
  TypographyComponent,
} from '@hyperkit'

import { VerityEnvironmentService } from '../environments/verity-environment.service'

import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { AuthBootstrapService } from './auth/auth-bootstrap.service'
import { AuthModule } from './auth/auth.module'
import { AuthService } from './auth/auth.service'
import { ReleaseModule } from './changelog/release.module'
import { ReleaseService } from './changelog/release.service'
import { FeatureFlagService } from './helpers/feature-flag.service'
import { FooterOnlyLayoutComponent } from './layouts/footer-only-layout/footer-only-layout.component'
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component'
import { ArchiveHuntGuard } from './modules/hunt/archived-hunt.guard'
import { HuntEditReportComponent } from './modules/hunt/hunt-edit-report/hunt-edit-report.component'
import { HuntFinalReportComponent } from './modules/hunt/hunt-final-report/hunt-final-report.component'
import { HuntWorkbenchComponent } from './modules/hunt/hunt-workbench/hunt-workbench.component'
import { HuntModule } from './modules/hunt/hunt.module'
import { ResearchModule } from './modules/research/research.module'
import { SettingsModule } from './modules/settings/settings.module'
import { FooterComponent } from './modules/shared/components/footer/footer.component'
import { HeaderComponent } from './modules/shared/components/header/header.component'
import { UserService } from './modules/shared/services/user.service'
import { SharedModule } from './modules/shared/shared.module'
import { FourOhFourComponent } from './screens/404/404.component'
import { ContactUsComponent } from './screens/customer-support/contact.component'
import { HuntGateComponent } from './screens/hunt-gate/hunt-gate.component'
import { LoginComponent } from './screens/portal/login/login.component'
import { NewPasswordComponent } from './screens/portal/new-password/new-password.component'
import { PortalComponent } from './screens/portal/portal.component'
import { ResetPasswordComponent } from './screens/portal/reset-password/reset-password.component'
import { PremiumGateComponent } from './screens/premium-gate/premium-gate.component'
import { ReactivateDialogComponent } from './screens/premium-gate/reactivate-dialog/reactivate-dialog.component'
import { PrivacyComponent } from './screens/privacy-policy/privacy.component'
import { TrialGateComponent } from './screens/trial-gate/trial-gate.component'
import { VerityRedirectComponent } from './screens/verity-redirect/verity-redirect.component'
import { TrackingModule } from './tracking/tracking.module'

@NgModule({
  declarations: [
    AppComponent,
    PortalComponent,
    LoginComponent,
    NewPasswordComponent,
    ResetPasswordComponent,
    HeaderComponent,
    ContactUsComponent,
    PrivacyComponent,
    TrialGateComponent,
    ReactivateDialogComponent,
    PremiumGateComponent,
    HuntGateComponent,
    VerityRedirectComponent,
    FourOhFourComponent,
    FooterComponent,
    FooterOnlyLayoutComponent,
    MainLayoutComponent,
  ],
  imports: [
    HuntModule,
    SharedModule,
    HyperkitComponent,
    ButtonComponent,
    TypographyComponent,
    CheckboxComponent,
    IconComponent,
    BadgeComponent,
    BannerComponent,
    MessageComponent,
    SearchInputComponent,
    LinkComponent,
    InputComponent,
    TextAreaComponent,
    SnackbarComponent,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    ErrorComponent,
    CheckboxComponent,
    HttpClientModule,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    RouterModule,
    MomentModule,
    NgChartsModule,
    ImageCropperModule,
    AuthModule,
    TrackingModule,
    SettingsModule,
    ResearchModule,
    FeatureToggleModule,
    ReleaseModule,
    // Hunt Module Redesign
    HuntWorkbenchComponent,
    HuntEditReportComponent,
    HuntFinalReportComponent,
  ],
  providers: [
    UserService,
    ThemeService,
    FeatureFlagService,
    ReleaseService,
    DialogService,
    ArchiveHuntGuard,
    AuthBootstrapService,
    provideAppInitializer(() => {
      const verityEnvironmentService = inject(VerityEnvironmentService)
    }),
    provideAppInitializer(() => {
      const authBootstrap = inject(AuthBootstrapService)
      return authBootstrap.init()
    }),
    provideAppInitializer(() => {
      const themeService = inject(ThemeService)
      return themeService.initializeTheme()
    }),
    provideAppInitializer(() => {
      const featureFlags = inject(FeatureFlagService)
      return featureFlags.loadFeatureFlags()
    }),
    { provide: ICON_SETTINGS, useValue: { type: 'font' } },
    provideAppInitializer(() => {
      const authService = inject(AuthService)
      return authService.checkStatus('premium-gate')
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
